/**
 * Нормализация клавиатурного ввода.
 *
 * Браузер отдаёт событие с модификаторами и служебными клавишами; движку
 * набора нужен один символ и монотонная метка времени. Разделение здесь
 * позволяет ядру не знать о DOM (docs/ARCHITECTURE.md §2).
 */

import type { ModifierState } from '@type2x/shared-contracts';

/** Действие, которое требуется выполнить по нажатию. */
export type InputAction =
  | { readonly kind: 'character'; readonly character: string; readonly modifiers: ModifierState }
  | { readonly kind: 'backspace' }
  | { readonly kind: 'ignore' };

/** Клавиши, которые не участвуют в наборе текста. */
const IGNORED_KEYS = new Set([
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'CapsLock',
  'Tab',
  'Escape',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Insert',
  'Delete',
  'ContextMenu',
  'NumLock',
  'ScrollLock',
  'Pause',
]);

/** Извлекает состояние модификаторов из события. */
export function modifiersOf(event: KeyboardEvent): ModifierState {
  return {
    shift: event.shiftKey,
    ctrl: event.ctrlKey,
    alt: event.altKey,
    meta: event.metaKey,
    capsLock: event.getModifierState?.('CapsLock') ?? false,
  };
}

/**
 * Преобразует событие клавиатуры в действие.
 *
 * Сочетания с Ctrl и Meta игнорируются: это команды браузера и системы, а не
 * ввод текста. Alt пропускается, потому что на части раскладок он участвует в
 * наборе символов.
 */
export function normalizeKeyEvent(event: KeyboardEvent): InputAction {
  if (event.key === 'Backspace') return { kind: 'backspace' };
  if (event.ctrlKey || event.metaKey) return { kind: 'ignore' };
  if (IGNORED_KEYS.has(event.key)) return { kind: 'ignore' };
  if (event.key === 'Enter') return { kind: 'ignore' };

  // Одиночный печатный символ; Unicode-символы длиннее одного code unit
  // приходят как строка длиной 2 и тоже допустимы.
  const characters = [...event.key];
  if (characters.length !== 1) return { kind: 'ignore' };

  return { kind: 'character', character: event.key, modifiers: modifiersOf(event) };
}

/**
 * Признаки программно сгенерированного события.
 *
 * Используется anti-cheat: доверенное событие имеет isTrusted === true
 * (ТЗ FR-VT-02). Решение о наказании принимает сервер, здесь только сигнал.
 */
export function isSyntheticEvent(event: KeyboardEvent): boolean {
  return event.isTrusted === false;
}
